<template>
  <div>
    <app-template />

    <!-- Profile Completion Check -->
    <ProfileCompletionCheck :show="showProfileCheck" :allowSkip="false" @complete="onProfileComplete" />

    <div class="uk-container uk-container-large main-body-padding">
      <!-- Header -->
      <div class="page-header">
        <h1>{{ pageTitle }}</h1>
        <p class="page-subtitle">{{ pageSubtitle }}</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <div class="spinner"></div>
        <p>Loading nutrition data...</p>
      </div>

      <!-- Main Content (only show if profile is complete) -->
      <template v-else-if="profileComplete">
        <!-- Foundation phase (U11–U15): welfare guardrail.
             No energy figures, weight entry or tracking UI is shown. The
             age-appropriate food-guidance surface is served here once built. -->
        <div v-if="isFoundation" class="foundation-panel tab-panel">
          <div class="foundation-card">
            <h3>Fuelling for Performance</h3>
            <p>
              Eating well around training and games helps you grow and play your
              best. Your coaches and sport science team will guide you and your
              parents on good food choices and staying hydrated.
            </p>
            <p class="foundation-note">
              Personalised nutrition guidance for your age group is coming soon.
            </p>
          </div>
        </div>

        <!-- Tab Navigation -->
        <div v-if="!isFoundation" class="tab-navigation">
          <button v-for="tab in tabs" :key="tab.id" class="tab-btn" :class="{ active: activeTab === tab.id }"
            @click="activeTab = tab.id">
            <span class="tab-icon">{{ tab.icon }}</span>
            <span class="tab-label">{{ tab.label }}</span>
          </button>
        </div>

        <!-- Tab Content -->
        <div v-if="!isFoundation" class="tab-content">
          <!-- Admin/Coach: Players Tab -->
          <div v-if="activeTab === 'players'" class="tab-panel">
            <!-- Show player detail if selected, otherwise show list -->
            <PlayerNutritionDetail v-if="selectedPlayer" :player="selectedPlayer" @back="handleBackFromPlayer" />
            <PlayerNutritionList v-else :date="currentDate" @select-player="handleSelectPlayer" />
          </div>

          <!-- Admin/Coach: Dashboard Tab -->
          <div v-if="activeTab === 'dashboard'" class="tab-panel">
            <NutritionDashboard :date="currentDate" @select-player="handleSelectPlayer" />
          </div>

          <!-- Today Tab (Players only) -->
          <div v-if="activeTab === 'today'" class="tab-panel">
            <!-- Reminders Alert Banner -->
            <NutritionReminders v-if="userId" :userId="userId" class="reminders-section" />

            <!-- Match Day Timeline (show on match days) -->
            <MatchDayTimeline v-if="isMatchDay" :userId="userId" :teamId="teamId" :date="currentDate"
              @select-meal="handleMealSelect" class="timeline-section" />

            <!-- Recovery Plan (show on recovery days) -->
            <RecoveryPlan v-if="isRecoveryDay" :userId="userId" :teamId="teamId" :date="currentDate"
              :activity="activity" :dailyTotals="dailyTotals" :dailyTargets="dailyTargets"
              @select-meal="handleMealSelect" class="recovery-section" />

            <DailyMealPlan :userId="userId" :clubId="clubId" :teamId="teamId" :weight="userWeight"
              @dateChange="handleDateChange" @activity-loaded="handleActivityLoaded"
              @totals-updated="handleTotalsUpdated" />

            <!-- Hydration Tracker -->
            <HydrationTracker v-if="userId" ref="hydrationTracker" :userId="userId" :teamId="teamId" :date="currentDate"
              :weight="userWeight" @hydration-logged="handleHydrationLogged" class="hydration-section" />
          </div>

          <!-- Week Tab -->
          <div v-if="activeTab === 'week'" class="tab-panel">
            <WeeklyOverview :userId="userId" :clubId="clubId" :teamId="teamId" :weight="userWeight"
              @selectDay="handleDaySelect" />
          </div>

          <!-- Recipes Tab -->
          <div v-if="activeTab === 'recipes'" class="tab-panel">
            <!-- Admin/Coach: Full recipe management -->
            <RecipeManager v-if="isAdmin || isCoach" />
            <!-- Player: Recipe list for browsing -->
            <RecipeList v-else :userId="userId" :initialContext="activity ? activity.context : null"
              @quick-add="handleQuickAddRecipe" />
          </div>

          <!-- Goals Tab -->
          <div v-if="activeTab === 'goals'" class="tab-panel">
            <div class="goals-section">
              <!-- Daily fuelling (floor framing — see useNutritionAccess.js) -->
              <div class="goals-card">
                <h3>Fuel for your day</h3>

                <!-- Under-18: parent/guardian consent required before figures -->
                <div v-if="needsConsent" class="fuel-consent">
                  <p class="goals-note">
                    Because this player is under 18, a parent or guardian needs
                    to give consent before we show personalised fuelling figures.
                    It's a welfare step — your details stay private to you and the
                    coaching staff.
                  </p>
                  <label class="consent-name">
                    <span>Parent / guardian name (optional)</span>
                    <input type="text" v-model="guardianName" placeholder="e.g. Alex Smith" />
                  </label>
                  <label class="consent-attest">
                    <input type="checkbox" v-model="consentAttested" />
                    <span>I am the player's parent or guardian and I give consent
                      for personalised fuelling guidance.</span>
                  </label>
                  <p v-if="consentError" class="fuel-error">{{ consentError }}</p>
                  <button class="consent-submit" :disabled="!consentAttested || savingConsent"
                    @click="submitParentConsent">
                    {{ savingConsent ? "Saving…" : "Give consent" }}
                  </button>
                </div>

                <!-- Consent fine, but no weight logged yet -->
                <p v-else-if="!hasWeight" class="goals-note goals-empty">
                  Add your current weight in
                  <router-link to="/settings">your profile</router-link>
                  to unlock your personalised fuelling guide.
                </p>

                <!-- Ready: show the guide -->
                <template v-else-if="fuelNumbersAllowed">
                  <p class="goals-note">
                    Aim to eat <strong>at least</strong> this to fuel your day —
                    more on hard days, never less. A guide based on your weight of
                    <strong>{{ displayWeight }} kg</strong>, not an exact figure.
                  </p>

                  <div class="targets-grid">
                    <div class="target-item">
                      <span class="target-icon carbs-icon">C</span>
                      <span class="target-label">Carbs</span>
                      <span class="target-value">≥ {{ calculatedTargets.carbs }}g</span>
                      <span class="target-formula">{{ defaultTargets.carbs }}g/kg</span>
                    </div>
                    <div class="target-item">
                      <span class="target-icon protein-icon">P</span>
                      <span class="target-label">Protein</span>
                      <span class="target-value">≥ {{ calculatedTargets.protein }}g</span>
                      <span class="target-formula">{{ defaultTargets.protein }}g/kg</span>
                    </div>
                    <div class="target-item">
                      <span class="target-icon fat-icon">F</span>
                      <span class="target-label">Fat</span>
                      <span class="target-value">≥ {{ calculatedTargets.fat }}g</span>
                      <span class="target-formula">{{ defaultTargets.fat }}g/kg</span>
                    </div>
                    <div class="target-item">
                      <span class="target-icon hydration-icon">H</span>
                      <span class="target-label">Hydration</span>
                      <span class="target-value">≥ {{ calculatedTargets.hydration }}mL</span>
                      <span class="target-formula">{{ defaultTargets.hydration }}mL/kg</span>
                    </div>
                  </div>
                </template>

                <!-- Access still loading -->
                <p v-else class="goals-note goals-empty">Loading your fuelling guide…</p>
              </div>

              <!-- Dietary Preferences -->
              <div class="goals-card">
                <h3>Dietary Preferences</h3>

                <div class="preferences-form">
                  <div class="form-group">
                    <label>Dietary Restrictions</label>
                    <div class="checkbox-group">
                      <label class="checkbox-item">
                        <input type="checkbox" v-model="preferences.vegetarian" />
                        <span>Vegetarian</span>
                      </label>
                      <label class="checkbox-item">
                        <input type="checkbox" v-model="preferences.vegan" />
                        <span>Vegan</span>
                      </label>
                      <label class="checkbox-item">
                        <input type="checkbox" v-model="preferences.glutenFree" />
                        <span>Gluten-Free</span>
                      </label>
                      <label class="checkbox-item">
                        <input type="checkbox" v-model="preferences.dairyFree" />
                        <span>Dairy-Free</span>
                      </label>
                      <label class="checkbox-item">
                        <input type="checkbox" v-model="preferences.halal" />
                        <span>Halal</span>
                      </label>
                    </div>
                  </div>

                  <div class="form-group">
                    <label>Allergies</label>
                    <textarea v-model="preferences.allergies"
                      placeholder="List any food allergies (e.g., nuts, shellfish)" rows="2"></textarea>
                  </div>

                  <div class="form-group">
                    <label>Notes</label>
                    <textarea v-model="preferences.notes" placeholder="Any other dietary notes or preferences"
                      rows="2"></textarea>
                  </div>

                  <button class="save-preferences-btn" @click="savePreferences" :disabled="savingPreferences">
                    {{ savingPreferences ? 'Saving...' : 'Save Preferences' }}
                  </button>
                </div>
              </div>

              <!-- Profile Info -->
              <div class="goals-card profile-info">
                <h3>Your Profile</h3>
                <div class="profile-stats">
                  <div class="stat">
                    <span class="stat-label">Height</span>
                    <span class="stat-value">{{ hasHeight ? displayHeight + ' cm' : '—' }}</span>
                  </div>
                  <div class="stat">
                    <span class="stat-label">Weight</span>
                    <span class="stat-value">{{ hasWeight ? displayWeight + ' kg' : '—' }}</span>
                  </div>
                  <div class="stat">
                    <span class="stat-label">Age</span>
                    <span class="stat-value">{{ userAge }} years</span>
                  </div>
                  <div class="stat">
                    <span class="stat-label">BMI</span>
                    <span class="stat-value">{{ bmi }}</span>
                  </div>
                </div>
                <router-link to="/settings" class="update-profile-link">
                  Update Profile
                </router-link>
              </div>
            </div>
          </div>

          <!-- Phase 3: Barcode Scanner Tab (Players) -->
          <div v-if="activeTab === 'scanner'" class="tab-panel">
            <BarcodeScanner :userId="userId" @food-logged="handleFoodLogged" />
          </div>

          <!-- Phase 3: Wearables Dashboard Tab (Players) -->
          <div v-if="activeTab === 'wearables'" class="tab-panel">
            <WearablesDashboard :userId="userId" :clubId="clubId" />
            <TrainingLoadCaloricCalculator v-if="userId" :userId="userId" :userWeight="userWeight"
              :userHeight="userHeight" :userAge="userAge" :gender="getProfile?.gender || 'male'"
              class="calculator-section" />
          </div>

          <!-- Phase 3: Supplements Tab (Players) -->
          <div v-if="activeTab === 'supplements'" class="tab-panel">
            <SupplementRecommendations :userId="userId" :context="activity?.context" :hasMatch="isMatchDay" />
          </div>

          <!-- Phase 3: Team Compliance Tab (Admin/Coach) -->
          <div v-if="activeTab === 'compliance'" class="tab-panel">
            <NutritionCopilot :clubId="clubId" :teamId="isCoach && !isAdmin ? teamId : null" />
            <ComplianceTrends :clubId="clubId" :teamId="isCoach && !isAdmin ? teamId : null" />
            <TeamComplianceDashboard :clubId="clubId" :teamId="teamId" @message-player="handleMessagePlayer"
              @view-profile="handleViewPlayerProfile" @send-reminder="handleSendReminder"
              @team-reminder="handleTeamReminder" @export-report="handleExportReport"
              @match-day-prep="handleMatchDayPrep" />
          </div>

          <!-- Admin/Coach: Fuelling Usage Tab -->
          <div v-if="activeTab === 'usage'" class="tab-panel">
            <FuellingUsage />
          </div>

          <!-- Admin/Coach: Fuelling Readiness Tab -->
          <div v-if="activeTab === 'readiness'" class="tab-panel">
            <FuellingReadiness />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import AppTemplate from "../layouts/App";
import ProfileCompletionCheck from "@/components/ProfileCompletionCheck/index.vue";
import DailyMealPlan from "@/components/Nutrition/DailyMealPlan.vue";
import WeeklyOverview from "@/components/Nutrition/WeeklyOverview.vue";
import RecipeList from "@/components/Nutrition/RecipeList.vue";
import MatchDayTimeline from "@/components/Nutrition/MatchDayTimeline.vue";
import HydrationTracker from "@/components/Nutrition/HydrationTracker.vue";
import RecoveryPlan from "@/components/Nutrition/RecoveryPlan.vue";
import NutritionReminders from "@/components/Nutrition/NutritionReminders.vue";
import PlayerNutritionList from "@/components/Nutrition/PlayerNutritionList.vue";
import PlayerNutritionDetail from "@/components/Nutrition/PlayerNutritionDetail.vue";
import NutritionDashboard from "@/components/Nutrition/NutritionDashboard.vue";
import RecipeManager from "@/components/Nutrition/RecipeManager.vue";
// Phase 3 - Advanced Integration
import BarcodeScanner from "@/components/Nutrition/BarcodeScanner.vue";
import WearablesDashboard from "@/components/Nutrition/WearablesDashboard.vue";
import TrainingLoadCaloricCalculator from "@/components/Nutrition/TrainingLoadCaloricCalculator.vue";
import SupplementRecommendations from "@/components/Nutrition/SupplementRecommendations.vue";
import TeamComplianceDashboard from "@/components/Nutrition/TeamComplianceDashboard.vue";
import FuellingUsage from "@/components/Nutrition/FuellingUsage.vue";
import FuellingReadiness from "@/components/Nutrition/FuellingReadiness.vue";
import ComplianceTrends from "@/components/Nutrition/ComplianceTrends.vue";
import NutritionCopilot from "@/components/Nutrition/NutritionCopilot.vue";
import { useProfileValidation } from "@/composables/useProfileValidation";
import { getNutritionAccess } from "@/composables/useNutritionAccess";
import { Nutrition } from "@/services/nutrition-service";

export default {
  name: "NutritionView",

  components: {
    AppTemplate,
    ProfileCompletionCheck,
    DailyMealPlan,
    WeeklyOverview,
    RecipeList,
    MatchDayTimeline,
    HydrationTracker,
    RecoveryPlan,
    NutritionReminders,
    PlayerNutritionList,
    PlayerNutritionDetail,
    NutritionDashboard,
    RecipeManager,
    // Phase 3 - Advanced Integration
    BarcodeScanner,
    WearablesDashboard,
    TrainingLoadCaloricCalculator,
    SupplementRecommendations,
    TeamComplianceDashboard,
    FuellingUsage,
    FuellingReadiness,
    ComplianceTrends,
    NutritionCopilot,
  },

  setup() {
    const {
      isProfileComplete,
      hasValidHeight,
      hasValidWeight,
      hasValidDob,
      age,
      bmi,
    } = useProfileValidation();

    return {
      isProfileComplete,
      hasValidHeight,
      hasValidWeight,
      hasValidDob,
      userAge: age,
      userBmi: bmi,
    };
  },

  data() {
    return {
      loading: true,
      profileComplete: false,
      showProfileCheck: false,
      activeTab: "today",
      currentDate: new Date().toISOString().split("T")[0],
      activity: null,
      dailyTotals: { total_carbs: 0, total_protein: 0, total_hydration: 0 },
      dailyTargets: { carbs: 350, protein: 112, hydration: 2450 },
      preferences: {
        vegetarian: false,
        vegan: false,
        glutenFree: false,
        dairyFree: false,
        halal: false,
        allergies: "",
        notes: "",
      },
      savingPreferences: false,
      defaultTargets: {
        carbs: 5,
        protein: 1.6,
        fat: 1.0,
        hydration: 35,
      },
      // Player fuelling welfare access (booleans only, from the server) + the
      // self-serve parent/guardian consent form for under-18s.
      fuellingAccess: null,
      guardianName: "",
      consentAttested: false,
      savingConsent: false,
      consentError: "",
      // Admin/Coach specific
      selectedPlayer: null,
    };
  },

  computed: {
    ...mapGetters(["getProfile"]),

    userId() {
      return this.getProfile?.id;
    },

    clubId() {
      return this.getProfile?.clubId;
    },

    teamId() {
      return this.getProfile?.teamId;
    },

    // Welfare guardrails — see useNutritionAccess.js / energy-fuelling-estimator-handoff.md
    nutritionAccess() {
      return getNutritionAccess(this.getProfile);
    },

    // Foundation-phase players (U11–U15, or unknown age) must not see energy
    // figures, weight entry or weight trends. Fail-safe is Foundation.
    isFoundation() {
      return this.nutritionAccess.isFoundation;
    },

    canSeeEnergyFigures() {
      return this.nutritionAccess.canSeeEnergyFigures;
    },

    // Under-18 Performance player who still needs parent/guardian consent
    // before any fuelling figures are shown (welfare parity with Flutter).
    needsConsent() {
      return this.fuellingAccess?.needs_consent === true;
    },

    // Server-confirmed permission to show this player fuelling numbers.
    fuelNumbersAllowed() {
      return this.fuellingAccess?.can_see_numbers === true;
    },

    userWeight() {
      return parseFloat(this.getProfile?.weight) || 70;
    },

    userHeight() {
      return parseFloat(this.getProfile?.height) || 175;
    },

    // Real profile values, with NO fabricated fallback. Used to decide whether
    // it is safe to SHOW body numbers to a player: we must never invent a
    // weight/BMI or fuelling figures for a player who hasn't given one (welfare
    // guardrail — see useNutritionAccess.js). userWeight/userHeight keep their
    // 70/175 fallback only for downstream child-component props.
    hasWeight() {
      const w = parseFloat(this.getProfile?.weight);
      return Number.isFinite(w) && w > 0;
    },

    hasHeight() {
      const h = parseFloat(this.getProfile?.height);
      return Number.isFinite(h) && h > 0;
    },

    displayWeight() {
      return this.hasWeight ? parseFloat(this.getProfile?.weight) : null;
    },

    displayHeight() {
      return this.hasHeight ? parseFloat(this.getProfile?.height) : null;
    },

    bmi() {
      if (!this.hasWeight || !this.hasHeight) return "—";
      const heightM = this.displayHeight / 100;
      return (this.displayWeight / (heightM * heightM)).toFixed(1);
    },

    calculatedTargets() {
      const weight = this.userWeight;
      return {
        carbs: Math.round(this.defaultTargets.carbs * weight),
        protein: Math.round(this.defaultTargets.protein * weight),
        fat: Math.round(this.defaultTargets.fat * weight),
        hydration: Math.round(this.defaultTargets.hydration * weight),
      };
    },

    isMatchDay() {
      return this.activity && this.activity.type === "match";
    },

    isRecoveryDay() {
      return this.activity && this.activity.context === "recovery_day";
    },

    isAdmin() {
      return this.getProfile?.userType === "Admin";
    },

    isCoach() {
      // Match useNutritionAccess.isStaff: a coach may be recorded via userType
      // OR position — check both so they aren't misrouted to the player view.
      const p = this.getProfile || {};
      return (
        p.userType === "Coach" ||
        p.position === "Coach" ||
        p.position === "HeadCoach"
      );
    },

    isPlayer() {
      return !this.isAdmin && !this.isCoach;
    },

    tabs() {
      if (this.isAdmin || this.isCoach) {
        return [
          { id: "players", label: "Players", icon: "👥" },
          { id: "readiness", label: "Readiness", icon: "🧮" },
          { id: "dashboard", label: "Dashboard", icon: "📊" },
          { id: "usage", label: "Usage", icon: "📈" },
          { id: "compliance", label: "Compliance", icon: "✅" },
          { id: "recipes", label: "Recipes", icon: "🍽️" },
        ];
      }
      return [
        { id: "today", label: "Today", icon: "📅" },
        { id: "week", label: "Week", icon: "📆" },
        { id: "scanner", label: "Scan Food", icon: "📷" },
        { id: "wearables", label: "Wearables", icon: "⌚" },
        { id: "supplements", label: "Supplements", icon: "💊" },
        { id: "recipes", label: "Recipes", icon: "🍽️" },
        { id: "goals", label: "Goals", icon: "🎯" },
      ];
    },

    pageTitle() {
      if (this.isAdmin || this.isCoach) {
        return "Team Nutrition";
      }
      return "Nutrition";
    },

    pageSubtitle() {
      if (this.isAdmin || this.isCoach) {
        return "Monitor player nutrition and manage recipes";
      }
      return "Track your meals and fuel your performance";
    },
  },

  watch: {
    getProfile: {
      immediate: true,
      handler() {
        this.checkProfile();
      },
    },
  },

  mounted() {
    this.checkProfile();
    this.loadPreferences();
    this.setDefaultTab();
    this.loadFuellingAccess();
  },

  methods: {
    setDefaultTab() {
      // Set default tab based on role
      if (this.isAdmin || this.isCoach) {
        this.activeTab = "players";
      } else {
        this.activeTab = "today";
      }
    },

    // Player-only: fetch this player's fuelling welfare access (booleans).
    async loadFuellingAccess() {
      if (this.isAdmin || this.isCoach || !this.userId) return;
      try {
        const res = await Nutrition.getMyFuellingAccess(this.userId);
        const payload = res.data ?? res;
        if (payload && payload.success) {
          this.fuellingAccess = payload.data;
        }
      } catch (e) {
        // Fail safe: leave access null (numbers stay hidden).
      }
    },

    // Player/parent self-serve consent (under-18s).
    async submitParentConsent() {
      if (!this.consentAttested || !this.userId) return;
      this.savingConsent = true;
      this.consentError = "";
      try {
        const res = await Nutrition.giveParentConsent(
          this.userId,
          this.guardianName || null
        );
        const payload = res.data ?? res;
        if (payload && payload.success) {
          this.consentAttested = false;
          await this.loadFuellingAccess();
        } else {
          this.consentError = (payload && payload.error) || "Could not save consent.";
        }
      } catch (e) {
        this.consentError = "Could not save consent.";
      } finally {
        this.savingConsent = false;
      }
    },

    checkProfile() {
      if (!this.getProfile) {
        this.loading = true;
        return;
      }

      this.loading = false;

      // Skip profile completion check for admins and coaches
      if (this.isAdmin || this.isCoach) {
        this.showProfileCheck = false;
        this.profileComplete = true;
        return;
      }

      // Welfare guardrail: never require Foundation-phase players to enter
      // weight. They get the food-guidance panel, not the tracking UI.
      if (this.isFoundation) {
        this.showProfileCheck = false;
        this.profileComplete = true;
        return;
      }

      // Check if profile is complete (players only)
      if (!this.isProfileComplete) {
        this.showProfileCheck = true;
        this.profileComplete = false;
      } else {
        this.showProfileCheck = false;
        this.profileComplete = true;
      }
    },

    onProfileComplete() {
      this.showProfileCheck = false;
      this.profileComplete = true;
      // Refresh profile data
      this.$store.dispatch("fetchProfile");
    },

    handleDateChange(date) {
      this.currentDate = date;
    },

    handleDaySelect(date) {
      this.currentDate = date;
      this.activeTab = "today";
    },

    handleActivityLoaded(activity) {
      this.activity = activity;
      // Update targets based on activity
      if (activity) {
        this.dailyTargets = { ...this.calculatedTargets };
      }
    },

    handleTotalsUpdated(totals) {
      this.dailyTotals = totals;
      // Refresh hydration tracker when meals are updated (hydration may have changed)
      if (this.$refs.hydrationTracker) {
        this.$refs.hydrationTracker.loadHydrationPlan();
      }
    },

    handleMealSelect(meal) {
      // Navigate to the meal logger or show quick-add modal
      console.log("Selected meal:", meal);
    },

    handleHydrationLogged(data) {
      // Update daily totals if needed
      this.dailyTotals.total_hydration =
        (this.dailyTotals.total_hydration || 0) + data.amount;
    },

    handleQuickAddRecipe(recipe) {
      // Open meal logger with pre-filled recipe data
      console.log("Quick add recipe:", recipe);
      // Could emit to DailyMealPlan or show a modal
    },

    // Admin/Coach methods
    handleSelectPlayer(player) {
      this.selectedPlayer = player;
      // If selecting from dashboard, switch to players tab
      if (this.activeTab !== "players") {
        this.activeTab = "players";
      }
    },

    handleBackFromPlayer() {
      this.selectedPlayer = null;
    },

    async loadPreferences() {
      if (!this.userId) return;

      try {
        const response = await Nutrition.getPlayerGoals(this.userId);
        if (response.data.success && response.data.data) {
          const goals = response.data.data;
          const restrictions = goals.dietary_restrictions || [];

          this.preferences = {
            vegetarian: restrictions.includes("vegetarian"),
            vegan: restrictions.includes("vegan"),
            glutenFree: restrictions.includes("gluten-free"),
            dairyFree: restrictions.includes("dairy-free"),
            halal: restrictions.includes("halal"),
            allergies: (goals.allergies || []).join(", "),
            notes: goals.notes || "",
          };
        }
      } catch (error) {
        console.error("Error loading preferences:", error);
      }
    },

    async savePreferences() {
      if (!this.userId || this.savingPreferences) return;

      this.savingPreferences = true;
      try {
        const restrictions = [];
        if (this.preferences.vegetarian) restrictions.push("vegetarian");
        if (this.preferences.vegan) restrictions.push("vegan");
        if (this.preferences.glutenFree) restrictions.push("gluten-free");
        if (this.preferences.dairyFree) restrictions.push("dairy-free");
        if (this.preferences.halal) restrictions.push("halal");

        const allergies = this.preferences.allergies
          ? this.preferences.allergies.split(",").map(a => a.trim()).filter(a => a)
          : [];

        const response = await Nutrition.savePlayerGoals({
          user_id: this.userId,
          dietary_restrictions: restrictions,
          allergies: allergies,
          notes: this.preferences.notes,
        });

        if (response.data.success) {
          this.$toast.success("Preferences saved");
        } else {
          this.$toast.error(response.data.error || "Failed to save preferences");
        }
      } catch (error) {
        console.error("Error saving preferences:", error);
        this.$toast.error("Failed to save preferences");
      } finally {
        this.savingPreferences = false;
      }
    },

    // =========================================================================
    // Phase 3: Event Handlers
    // =========================================================================

    handleFoodLogged(foodData) {
      // Refresh daily plan when food is scanned and logged
      console.log("Food logged from scanner:", foodData);
      this.$toast.success(`${foodData.food_name} added to meal log`);
      // Could refresh daily totals here
    },

    handleMessagePlayer(player) {
      // Open messaging for a specific player
      console.log("Message player:", player);
      // Navigate to chat or open message modal
      this.$router.push({ name: "chat", query: { userId: player.id } });
    },

    handleViewPlayerProfile(player) {
      // View full player profile
      this.handleSelectPlayer(player);
    },

    handleSendReminder(player) {
      // Send nutrition reminder to player
      Nutrition.sendPlayerNutritionReminder(player.id)
        .then((response) => {
          if (response.data?.success) {
            this.$toast.success(`Reminder sent to ${player.name}`);
          }
        })
        .catch((error) => {
          console.error("Error sending reminder:", error);
          this.$toast.error("Failed to send reminder");
        });
    },

    handleTeamReminder() {
      // Send nutrition reminder to entire team
      Nutrition.sendTeamNutritionReminder(this.clubId, this.teamId)
        .then((response) => {
          if (response.data?.success) {
            this.$toast.success("Team reminder sent");
          }
        })
        .catch((error) => {
          console.error("Error sending team reminder:", error);
          this.$toast.error("Failed to send team reminder");
        });
    },

    handleExportReport(reportData) {
      // Export nutrition compliance report
      Nutrition.exportComplianceReport(
        this.clubId,
        reportData.teamId,
        { start: reportData.weekStart, end: reportData.weekEnd },
        "csv"
      )
        .then((response) => {
          if (response.data?.success && response.data.download_url) {
            window.open(response.data.download_url, "_blank");
            this.$toast.success("Report exported");
          }
        })
        .catch((error) => {
          console.error("Error exporting report:", error);
          this.$toast.error("Failed to export report");
        });
    },

    handleMatchDayPrep() {
      // Navigate to match day nutrition prep view
      console.log("Match day prep clicked");
      // Could open a modal or navigate to a specific view
    },
  },
};
</script>

<style scoped lang="scss">
.page-header {
  text-align: center;
  margin-bottom: 24px;
  padding-top: 20px;

  h1 {
    margin: 0 0 8px;
    font-size: 1.8rem;
    color: #333;
  }

  .page-subtitle {
    margin: 0;
    color: #666;
    font-size: 1rem;
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px;
  color: #666;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #f0f0f0;
    border-top-color: #007bff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
}

.tab-navigation {
  display: flex;
  gap: 8px;
  background: white;
  padding: 8px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
}

.tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: transparent;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f0f0f0;
  }

  &.active {
    background: #007bff;
    color: white;

    .tab-icon {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  .tab-icon {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    background: #e9ecef;
  }
}

/* Phase 2 Section Styles */
.reminders-section {
  margin-bottom: 16px;
}

.timeline-section {
  margin-bottom: 20px;
}

.recovery-section {
  margin-bottom: 20px;
}

.hydration-section {
  margin-top: 20px;
}

.tab-content {
  min-height: 400px;
}

.tab-panel {
  animation: fadeIn 0.3s ease;
}

.foundation-card {
  max-width: 560px;
  margin: 24px auto;
  padding: 28px 24px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  text-align: center;
}

.foundation-card h3 {
  margin: 0 0 12px;
}

.foundation-card .foundation-note {
  margin-top: 16px;
  font-style: italic;
  color: #6b7280;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* Goals Tab */
.goals-section {
  max-width: 600px;
  margin: 0 auto;
}

.goals-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 16px;

  h3 {
    margin: 0 0 12px;
    font-size: 1.1rem;
    color: #333;
  }
}

.goals-note {
  margin: 0 0 16px;
  font-size: 0.9rem;
  color: #666;
}

/* Under-18 self-serve parent/guardian consent panel */
.fuel-consent {
  background: #f8f9fb;
  border: 1px solid #e6e9ef;
  border-radius: 12px;
  padding: 16px;
}
.fuel-consent .consent-name {
  display: block;
  margin: 4px 0 12px;
}
.fuel-consent .consent-name span {
  display: block;
  font-size: 0.8rem;
  color: #555;
  margin-bottom: 4px;
}
.fuel-consent .consent-name input {
  width: 100%;
  max-width: 320px;
  border: 1px solid #d5d9dd;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.9rem;
  color: #2c3136;
}
.fuel-consent .consent-attest {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.86rem;
  color: #333;
  line-height: 1.4;
  cursor: pointer;
  margin-bottom: 12px;
}
.fuel-consent .consent-attest input {
  width: auto;
  margin-top: 3px;
  flex: 0 0 auto;
}
.fuel-consent .consent-submit {
  background: var(--brand-color, #2b61d6);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
}
.fuel-consent .consent-submit:disabled {
  opacity: 0.5;
  cursor: default;
}
.fuel-error {
  color: #c0392b;
  font-size: 0.84rem;
  margin: 4px 0 8px;
}

.targets-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.target-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 10px;

  .target-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    font-weight: 700;
    color: white;
    margin-bottom: 8px;

    &.carbs-icon {
      background: linear-gradient(135deg, #ffc107, #ff9800);
    }

    &.protein-icon {
      background: linear-gradient(135deg, #dc3545, #c82333);
    }

    &.fat-icon {
      background: linear-gradient(135deg, #28a745, #20c997);
    }

    &.hydration-icon {
      background: linear-gradient(135deg, #007bff, #17a2b8);
    }
  }

  .target-label {
    font-size: 0.8rem;
    color: #666;
    margin-bottom: 4px;
  }

  .target-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #333;
  }

  .target-formula {
    font-size: 0.7rem;
    color: #999;
  }
}

.preferences-form {
  .form-group {
    margin-bottom: 16px;

    > label {
      display: block;
      font-weight: 600;
      color: #333;
      margin-bottom: 8px;
      font-size: 0.9rem;
    }

    textarea {
      width: 100%;
      padding: 10px 12px;
      border: 1px solid #ddd;
      border-radius: 8px;
      font-size: 0.95rem;
      resize: vertical;

      &:focus {
        outline: none;
        border-color: #007bff;
      }
    }
  }
}

.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  color: #333;
  cursor: pointer;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
}

.save-preferences-btn {
  width: 100%;
  padding: 12px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #1e7e34;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
}

.profile-info {
  .profile-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 16px;
  }

  .stat {
    text-align: center;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 8px;

    .stat-label {
      display: block;
      font-size: 0.75rem;
      color: #666;
      margin-bottom: 4px;
    }

    .stat-value {
      font-size: 1.1rem;
      font-weight: 700;
      color: #333;
    }
  }

  .update-profile-link {
    display: block;
    text-align: center;
    color: #007bff;
    font-size: 0.9rem;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

/* Dashboard placeholder */
.dashboard-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  .placeholder-icon {
    color: #ccc;
    margin-bottom: 20px;
  }

  h3 {
    margin: 0 0 8px;
    font-size: 1.2rem;
    color: #333;
  }

  p {
    margin: 0;
    color: #666;
    font-size: 0.95rem;
    max-width: 300px;
  }
}

/* Mobile adjustments */
@media screen and (max-width: 480px) {
  .page-header {
    padding-top: 16px;
    margin-bottom: 16px;

    h1 {
      font-size: 1.5rem;
    }
  }

  .tab-navigation {
    padding: 6px;
    gap: 4px;
  }

  .tab-btn {
    padding: 10px 8px;
    font-size: 0.85rem;

    .tab-label {
      display: none;
    }

    .tab-icon {
      font-size: 1.3em;
    }
  }

  .targets-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .target-item {
    padding: 12px;

    .target-value {
      font-size: 1.1rem;
    }
  }

  .profile-info .profile-stats {
    grid-template-columns: repeat(2, 1fr);
  }
}

// Phase 3 Section Styling
.calculator-section {
  margin-top: 24px;
}

.tab-panel {
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
